import { DateTime } from "luxon";

import {
  CaseDetailState,
  CaseLogEventType,
  CaseLogItem,
} from "shared/reducers/caseDetailReducer";
import { caseSlideLabel } from "shared/utils/slideUtils";

import { ReactComponent as DocumentIcon } from "icons/document.svg";
import { ReactComponent as NewSlideIcon } from "icons/new-slide.svg";

import LinkButton from "../LinkButton";

interface CaseLogProps {
  amended: boolean;
  caseDetail: CaseDetailState;
}

export default function CaseLog(props: CaseLogProps) {
  const case_log: CaseLogItem[] = props.caseDetail.case_log || [];

  const slideDescriptors: Map<number, string> = props.caseDetail.slides.reduce(
    (acc, e) => {
      acc.set(e.id, caseSlideLabel(e, props.caseDetail));
      return acc;
    },
    new Map()
  );

  return (
    <>
      {case_log.map((caseLogItem) => {
        const authorName = caseLogItem.author
          ? `${caseLogItem.author.first_name} ${caseLogItem.author.last_name}`
          : "Unknown User";
        const associatedUserName = caseLogItem.associated_user
          ? `${caseLogItem.associated_user.first_name} ${caseLogItem.associated_user.last_name}`
          : "Unknown User";
        const formattedTime = caseLogItem.created_at
          ? DateTime.fromISO(caseLogItem.created_at).toLocaleString()
          : "";
        const slide = caseLogItem.slide;

        const descriptor =
          (slide && slideDescriptors.get(slide.id)) || "[Slide not found]";

        switch (caseLogItem.event) {
          // received-sign-out - no users, no slide, "Case finalized in WebPathLabs"
          case CaseLogEventType.RECEIVED_SIGN_OUT:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="WPL Sign Out">
                  <span className="Chat__step Chat__step--received-sign-out">
                    WPL Sign Out <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>Case finalized in WebPathLabs.</span>
              </article>
            );
          // accession-done - no users, no slide, "Accessioning Completed"
          case CaseLogEventType.ACCESSION_DONE:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Accession Done">
                  <span className="Chat__step Chat__step--accession-done">
                    Accession Done <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>Accessioning completed.</span>
              </article>
            );
          // gross-done - no users, no slide, "Grossing Completed"
          case CaseLogEventType.GROSS_DONE:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Gross Done">
                  <span className="Chat__step Chat__step--gross-done">
                    Gross Done <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>Grossing Completed.</span>
              </article>
            );
          // rescan-archival - author, "<user first> <user last> requested rescan for slide <specimen name><slide block>.<slide slide_number>"
          case CaseLogEventType.RESCAN_ARCHIVAL:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Rescan Requested">
                  <span className="Chat__step Chat__step--rescan-archival">
                    Rescan Requested <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>{`${authorName} requested rescan for slide ${descriptor}.`}</span>
              </article>
            );
          // slide-added - no users, slide available, " <specimen name><slide block>.<slide slide_number> added"  # this will be the received from WPL created, not "visible on case"
          case CaseLogEventType.SLIDE_ADDED:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Slide Added">
                  <span className="Chat__step Chat__step--slide-added">
                    Slide Added <NewSlideIcon className="icon-20" />
                  </span>
                </LinkButton>
                <span>{`${descriptor || "Slide has no data"}`}</span>
              </article>
            );
          // archived-by-clinic-rule - no users, slide available, "<specimen name><slide block>.<slide slide_number> archived by clinic rule"
          case CaseLogEventType.ARCHIVED_BY_CLINIC_RULE:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Archived">
                  <span className="Chat__step Chat__step--archived-by-clinic-rule">
                    Archived <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>{`${descriptor} archived by clinic rule.`}</span>
              </article>
            );
          // assign-screener - author and associated user, no slide, "<user first> <user last> assigned <associated first> <associated last> to pre-pathologist screen"
          case CaseLogEventType.ASSIGN_SCREENER:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Screening Pathologist Assigned">
                  <span className="Chat__step Chat__step--assign-screener">
                    Screening Pathologist Assigned{" "}
                    <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>
                  {authorName} assigned {associatedUserName} to pre-pathologist
                  screen.
                </span>
              </article>
            );
          // screen-done - author, no slide, "screened by <user first> <user last>"
          case CaseLogEventType.SCREEN_DONE:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Screening Completed">
                  <span className="Chat__step Chat__step--screen-done">
                    Screening Completed <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>Screened by {authorName}.</span>
              </article>
            );
          // assign-signer - author and associated user, no slide, "<user first> <user last> assigned <associated first> <associated last> to pre-dermatologist screen"
          case CaseLogEventType.ASSIGN_SIGNER:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Signing Pathologist Assigned">
                  <span className="Chat__step Chat__step--assign-signer">
                    Signing Pathologist Assigned{" "}
                    <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>
                  {authorName} assigned {associatedUserName} to
                  pre-dermatologist screen.
                </span>
              </article>
            );
          // signed-out - author, no slide, "signed out by <user first> <user last>"
          case CaseLogEventType.SIGNED_OUT:
            return (
              <article key={caseLogItem.id}>
                <time>{formattedTime}</time>
                <LinkButton title="Case Signed Out">
                  <span className="Chat__step Chat__step--signed-out">
                    Case Signed Out <DocumentIcon className="icon-12" />
                  </span>
                </LinkButton>
                <span>Signed out by {authorName}.</span>
              </article>
            );
          default:
            return <></>;
        }
      })}
    </>
  );
}
